import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Shield, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  Filter,
  Eye,
  FileText,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Activity
} from 'lucide-react';
import { qualityInspections, receivingDashboard, inspectionStandards } from '../../data/receivingData';

const QualityInspection = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const renderWorkflowBanner = () => {
    if (navigationSource === 'purchase-workflow') {
      return (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-orange-800">來自業務流程: 採購週期管理</h3>
                <p className="text-sm text-orange-600">貨物接收與品質檢驗階段</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回採購管理</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredInspections = qualityInspections.filter(inspection => {
    const matchesSearch = inspection.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inspection.inspection_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inspection.inspector.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inspection.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">品質檢驗管理</h1>
          <p className="text-gray-600 mt-1">管理進料檢驗和品質控制</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>檢驗清單</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待檢驗項目</p>
              <p className="text-2xl font-bold text-gray-900">{receivingDashboard.pending_inspections.length}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
            <span className="text-sm text-yellow-600">需要關注</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">品質合格率</p>
              <p className="text-2xl font-bold text-gray-900">{receivingDashboard.summary.quality_pass_rate}%</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-sm text-green-600">+2.1% 比上月</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">檢驗總數</p>
              <p className="text-2xl font-bold text-gray-900">{qualityInspections.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <BarChart3 className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-sm text-blue-600">本月數據</span>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">不合格項目</p>
              <p className="text-2xl font-bold text-gray-900">
                {qualityInspections.filter(i => i.status === 'failed').length}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            <span className="text-sm text-red-600">-15% 比上月</span>
          </div>
        </div>
      </div>

      {/* Recent Inspections */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">最近檢驗記錄</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  檢驗編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  產品名稱
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  檢驗員
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  檢驗日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {qualityInspections.slice(0, 5).map((inspection) => (
                <tr key={inspection.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{inspection.inspection_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{inspection.product_name}</div>
                    <div className="text-sm text-gray-500">批號: {inspection.batch_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {inspection.inspector}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(inspection.inspection_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inspection.status)}`}>
                      {getStatusIcon(inspection.status)}
                      <span>
                        {inspection.status === 'passed' && '合格'}
                        {inspection.status === 'failed' && '不合格'}
                        {inspection.status === 'in_progress' && '檢驗中'}
                        {inspection.status === 'pending' && '待檢驗'}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedInspection(inspection.id);
                        setActiveView('detail');
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quality Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">品質趨勢</h3>
          <div className="space-y-4">
            {receivingDashboard.quality_trends.monthly_pass_rates.map((trend, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{trend.month}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${trend.pass_rate}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{trend.pass_rate}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">缺陷分析</h3>
          <div className="space-y-4">
            {receivingDashboard.quality_trends.defect_categories.map((category, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{category.category}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full" 
                      style={{ width: `${category.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{category.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderInspectionList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">品質檢驗清單</h1>
          <p className="text-gray-600 mt-1">所有品質檢驗記錄</p>
        </div>
        <button
          onClick={() => setActiveView('dashboard')}
          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回儀表板</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜尋檢驗編號、產品或檢驗員..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">所有狀態</option>
                <option value="pending">待檢驗</option>
                <option value="in_progress">檢驗中</option>
                <option value="passed">合格</option>
                <option value="failed">不合格</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Inspection List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  檢驗編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  產品資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  檢驗資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  數量
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInspections.map((inspection) => (
                <tr key={inspection.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{inspection.inspection_number}</div>
                    <div className="text-sm text-gray-500">{inspection.receiving_id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{inspection.product_name}</div>
                    <div className="text-sm text-gray-500">批號: {inspection.batch_number}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">檢驗員: {inspection.inspector}</div>
                    <div className="text-sm text-gray-500">
                      日期: {new Date(inspection.inspection_date).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">總量: {inspection.total_quantity.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">樣本: {inspection.sample_size}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inspection.status)}`}>
                      {getStatusIcon(inspection.status)}
                      <span>
                        {inspection.status === 'passed' && '合格'}
                        {inspection.status === 'failed' && '不合格'}
                        {inspection.status === 'in_progress' && '檢驗中'}
                        {inspection.status === 'pending' && '待檢驗'}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedInspection(inspection.id);
                        setActiveView('detail');
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      查看詳情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderInspectionDetail = () => {
    const inspection = qualityInspections.find(i => i.id === selectedInspection);
    if (!inspection) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">檢驗詳情</h1>
            <p className="text-gray-600 mt-1">{inspection.inspection_number}</p>
          </div>
          <button
            onClick={() => setActiveView('list')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回清單</span>
          </button>
        </div>

        {/* Inspection Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">基本資訊</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">檢驗編號:</span>
                <span className="text-gray-900 font-medium">{inspection.inspection_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">產品名稱:</span>
                <span className="text-gray-900 font-medium">{inspection.product_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">批號:</span>
                <span className="text-gray-900 font-medium">{inspection.batch_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">檢驗員:</span>
                <span className="text-gray-900 font-medium">{inspection.inspector}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">檢驗日期:</span>
                <span className="text-gray-900 font-medium">
                  {new Date(inspection.inspection_date).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">檢驗計劃:</span>
                <span className="text-gray-900 font-medium">{inspection.inspection_plan}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">檢驗結果</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">整體狀態:</span>
                <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inspection.status)}`}>
                  {getStatusIcon(inspection.status)}
                  <span>
                    {inspection.status === 'passed' && '合格'}
                    {inspection.status === 'failed' && '不合格'}
                    {inspection.status === 'in_progress' && '檢驗中'}
                    {inspection.status === 'pending' && '待檢驗'}
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">總數量:</span>
                <span className="text-gray-900 font-medium">{inspection.total_quantity.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">樣本數:</span>
                <span className="text-gray-900 font-medium">{inspection.sample_size}</span>
              </div>
              {inspection.certificate_issued && (
                <div className="flex justify-between">
                  <span className="text-gray-600">證書編號:</span>
                  <span className="text-gray-900 font-medium">{inspection.certificate_number}</span>
                </div>
              )}
              {inspection.approved_by && (
                <div className="flex justify-between">
                  <span className="text-gray-600">核准人:</span>
                  <span className="text-gray-900 font-medium">{inspection.approved_by}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Test Results */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">測試結果</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    測試項目
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    規格要求
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    測試結果
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    測試者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    狀態
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inspection.tests.map((test) => (
                  <tr key={test.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{test.test_name}</div>
                      <div className="text-sm text-gray-500">{test.test_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{test.specification}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{test.result}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{test.tested_by}</div>
                      {test.tested_at && (
                        <div className="text-sm text-gray-500">
                          {new Date(test.tested_at).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(test.status)}`}>
                        {getStatusIcon(test.status)}
                        <span>
                          {test.status === 'passed' && '合格'}
                          {test.status === 'failed' && '不合格'}
                          {test.status === 'in_progress' && '進行中'}
                          {test.status === 'pending' && '待測試'}
                        </span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Defects and Corrective Actions */}
        {(inspection.defects.length > 0 || inspection.corrective_actions.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {inspection.defects.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">缺陷記錄</h3>
                <div className="space-y-4">
                  {inspection.defects.map((defect) => (
                    <div key={defect.id} className="border-l-4 border-red-500 pl-4">
                      <div className="text-sm font-medium text-gray-900">{defect.description}</div>
                      <div className="text-sm text-gray-500">類型: {defect.type}</div>
                      <div className="text-sm text-gray-500">嚴重程度: {defect.severity}</div>
                      <div className="text-sm text-gray-500">影響數量: {defect.quantity_affected}</div>
                      <div className="text-sm text-gray-500">根本原因: {defect.root_cause}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {inspection.corrective_actions.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">矯正措施</h3>
                <div className="space-y-4">
                  {inspection.corrective_actions.map((action) => (
                    <div key={action.id} className="border-l-4 border-blue-500 pl-4">
                      <div className="text-sm font-medium text-gray-900">{action.action}</div>
                      <div className="text-sm text-gray-500">負責人: {action.responsible}</div>
                      <div className="text-sm text-gray-500">到期日: {action.due_date}</div>
                      <div className="text-sm text-gray-500">
                        狀態: 
                        <span className={`ml-1 ${action.status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                          {action.status === 'completed' ? '已完成' : '進行中'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {inspection.notes && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">備註</h3>
            <p className="text-gray-700">{inspection.notes}</p>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderInspectionList();
      case 'detail':
        return renderInspectionDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      {renderContent()}
    </div>
  );
};

export default QualityInspection; 